import { IconButton, LuIcon } from "@overmap-ai/blocks"
import { cx } from "class-variance-authority"
import { LngLat } from "mapbox-gl"
import { memo, useCallback, useRef } from "react"
import type { MarkerInstance } from "react-map-gl/mapbox"

import { modelIconMapping } from "../../mappings/icons"
import { Marker } from "./memo"

export type LayoutFileCorner = "northWest" | "northEast" | "southWest" | "southEast" | "center"

export type HandlePositionChangeEvent = {
	position: LngLat
	corner: LayoutFileCorner
}

export interface LayoutFileCornerHandleProps {
	position: LngLat
	onPositionChange: (e: HandlePositionChangeEvent) => void
	corner: LayoutFileCorner
}

export const LayoutFileCornerHandle = memo<LayoutFileCornerHandleProps>((props) => {
	const { position, onPositionChange, corner } = props
	const markerRef = useRef<MarkerInstance>(null)

	const handleDragEvent = useCallback(() => {
		if (!markerRef.current) return
		const marker = markerRef.current
		const lngLat = marker.getLngLat()
		onPositionChange({ position: lngLat, corner })
	}, [corner, onPositionChange])

	const getIcon = () => {
		switch (corner) {
			case "center":
				return <LuIcon icon={modelIconMapping.move} />
			default:
				return <LuIcon icon={modelIconMapping.expand} />
		}
	}

	const isCenter = corner === "center"
	const isNWorSE = ["northWest", "southEast"].includes(corner)
	const isNEorSW = ["northEast", "southWest"].includes(corner)

	return (
		<Marker
			anchor="center"
			ref={markerRef}
			longitude={position.lng}
			latitude={position.lat}
			draggable
			// onDrag={handleDragEvent}
			onDragEnd={handleDragEvent}
		>
			<IconButton
				size="xs"
				className={cx("absolute left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2", {
					"rotate-90": isNWorSE,
					"cursor-nwse-resize": isNWorSE,
					"cursor-nesw-resize": isNEorSW,
					"cursor-grab": isCenter,
				})}
			>
				{getIcon()}
			</IconButton>
		</Marker>
	)
})
LayoutFileCornerHandle.displayName = "LayoutFileCornerHandle"
